import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Filter, X, MapPin, Navigation, Loader2, LayoutGrid, Map } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ItemCard from '../components/ItemCard';
import ItemMapView from '../components/ItemMapView';
import { getItems, getCategories, getNeighborhoods, getZipcodes, getWishlistIds } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const DISTANCE_OPTIONS = [
  { label: 'Any distance', value: '' },
  { label: 'Within 1 mi', value: '1.6' },
  { label: 'Within 5 mi', value: '8' },
  { label: 'Within 10 mi', value: '16' },
  { label: 'Within 25 mi', value: '40' },
  { label: 'Within 50 mi', value: '80' },
];

const BrowsePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [zipcodes, setZipcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [userLocation, setUserLocation] = useState(null);
  const [wishlistIds, setWishlistIds] = useState([]);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    neighborhood: searchParams.get('neighborhood') || '',
    zipcode: searchParams.get('zipcode') || '',
    maxDistance: searchParams.get('maxDistance') || '',
  });

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [cats, hoods, zips] = await Promise.all([
          getCategories(),
          getNeighborhoods(),
          getZipcodes()
        ]);
        setCategories(cats);
        setNeighborhoods(hoods);
        setZipcodes(zips);
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    getWishlistIds().then(setWishlistIds).catch((err) => console.error('Failed to fetch wishlist IDs:', err));
  }, [isAuthenticated]);

  const handleWishlistChange = (itemId, action) => {
    setWishlistIds(prev => action === 'added' ? [...prev, itemId] : prev.filter(id => id !== itemId));
  };

  // Detect user GPS for distance filtering
  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }

    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        toast.success('Location detected');
        setDetectingLocation(false);

        // Also try to get the zipcode
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`,
            { headers: { 'User-Agent': 'NeighborBorrow/1.0' } }
          );
          const data = await response.json();
          if (data?.address?.postcode) {
            setFilters(prev => ({ ...prev, zipcode: data.address.postcode }));
          }
        } catch (err) {
          console.error('Failed to reverse geocode:', err);
        }
      },
      () => {
        setDetectingLocation(false);
        toast.error('Location access denied');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.search) params.search = filters.search;
        if (filters.category) params.category = filters.category;
        if (filters.neighborhood) params.neighborhood = filters.neighborhood;
        if (filters.zipcode) params.zipcode = filters.zipcode;

        // Distance filtering — needs user coords
        if (userLocation) {
          params.latitude = userLocation.lat;
          params.longitude = userLocation.lng;
          if (filters.maxDistance) {
            params.max_distance_km = parseFloat(filters.maxDistance);
          }
        }

        const data = await getItems(params);
        setItems(data);
      } catch (error) {
        console.error('Failed to fetch items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [filters, userLocation]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    const params = new URLSearchParams();
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.neighborhood) params.set('neighborhood', newFilters.neighborhood);
    if (newFilters.zipcode) params.set('zipcode', newFilters.zipcode);
    if (newFilters.maxDistance) params.set('maxDistance', newFilters.maxDistance);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', neighborhood: '', zipcode: '', maxDistance: '' });
    setSearchParams({});
  };

  const hasActiveFilters = filters.category || filters.neighborhood || filters.search || filters.zipcode || filters.maxDistance;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>Browse Items to Rent | NeighborBorrow</title>
        <meta name="description" content="Browse tools, electronics, books, sports gear, and more available for rent in your neighborhood. Filter by category, distance, and price." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#2C3329] mb-2">
              Browse Items
            </h1>
            <p className="text-[#596652]">Find what you need from your neighbors</p>
          </div>

          {/* View Mode Toggle */}
          <div className="hidden sm:flex bg-white border border-[#EBE8DF] rounded-xl p-1" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-[#D97757] text-white'
                  : 'text-[#596652] hover:text-[#2C3329]'
              }`}
              data-testid="grid-view-button"
            >
              <LayoutGrid className="w-4 h-4" />
              Grid
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-[#D97757] text-white'
                  : 'text-[#596652] hover:text-[#2C3329]'
              }`}
              data-testid="map-view-button"
            >
              <Map className="w-4 h-4" />
              Map
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#596652]" />
              <Input
                type="text"
                placeholder="Search items..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                data-testid="search-input"
              />
            </div>

            {/* Desktop Filters */}
            <div className="hidden sm:flex items-center gap-3">
              <Select
                value={filters.category || 'all'}
                onValueChange={(value) => handleFilterChange('category', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-40 bg-white border-[#EBE8DF] rounded-xl" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.neighborhood || 'all'}
                onValueChange={(value) => handleFilterChange('neighborhood', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-40 bg-white border-[#EBE8DF] rounded-xl" data-testid="neighborhood-filter">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas</SelectItem>
                  {neighborhoods.map((hood) => (
                    <SelectItem key={hood} value={hood}>{hood}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Distance Filter */}
              <Select
                value={filters.maxDistance || 'any'}
                onValueChange={(value) => {
                  if (value !== 'any' && !userLocation) {
                    detectLocation();
                  }
                  handleFilterChange('maxDistance', value === 'any' ? '' : value);
                }}
              >
                <SelectTrigger className="w-40 bg-white border-[#EBE8DF] rounded-xl" data-testid="distance-filter">
                  <SelectValue placeholder="Any distance" />
                </SelectTrigger>
                <SelectContent>
                  {DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value || 'any'} value={opt.value || 'any'}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Zipcode Filter */}
              <div className="flex items-center gap-1">
                <Input
                  type="text"
                  placeholder="Zip"
                  value={filters.zipcode}
                  onChange={(e) => handleFilterChange('zipcode', e.target.value)}
                  className="w-24 bg-white border-[#EBE8DF] rounded-xl"
                  data-testid="zipcode-filter"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={detectLocation}
                  disabled={detectingLocation}
                  className="text-[#D97757] hover:bg-[#D97757]/10"
                  title="Use my location"
                  data-testid="detect-location-button"
                >
                  {detectingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Navigation className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-[#596652] hover:text-[#D97757]"
                  data-testid="clear-filters-button"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Mobile Filter Toggle */}
            <div className="flex sm:hidden gap-2">
              <Button
                variant="outline"
                className="flex-1 border-[#EBE8DF] rounded-xl"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="mobile-filter-toggle"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 w-5 h-5 bg-[#D97757] text-white rounded-full text-xs flex items-center justify-center">
                    {[filters.category, filters.neighborhood, filters.zipcode, filters.maxDistance].filter(Boolean).length}
                  </span>
                )}
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                className={`border-[#EBE8DF] rounded-xl ${viewMode === 'map' ? 'bg-[#D97757] text-white hover:bg-[#C56547]' : ''}`}
                onClick={() => setViewMode(viewMode === 'grid' ? 'map' : 'grid')}
                data-testid="mobile-view-toggle"
              >
                {viewMode === 'grid' ? <Map className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="sm:hidden mt-4 p-4 bg-white rounded-xl border border-[#EBE8DF] space-y-4">
              <Select
                value={filters.category || 'all'}
                onValueChange={(value) => handleFilterChange('category', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-full bg-[#FDFBF7] border-[#EBE8DF] rounded-xl" data-testid="mobile-category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.neighborhood || 'all'}
                onValueChange={(value) => handleFilterChange('neighborhood', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-full bg-[#FDFBF7] border-[#EBE8DF] rounded-xl" data-testid="mobile-neighborhood-filter">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas</SelectItem>
                  {neighborhoods.map((hood) => (
                    <SelectItem key={hood} value={hood}>{hood}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Distance Filter Mobile */}
              <Select
                value={filters.maxDistance || 'any'}
                onValueChange={(value) => {
                  if (value !== 'any' && !userLocation) detectLocation();
                  handleFilterChange('maxDistance', value === 'any' ? '' : value);
                }}
              >
                <SelectTrigger className="w-full bg-[#FDFBF7] border-[#EBE8DF] rounded-xl" data-testid="mobile-distance-filter">
                  <SelectValue placeholder="Any distance" />
                </SelectTrigger>
                <SelectContent>
                  {DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value || 'any'} value={opt.value || 'any'}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Zipcode Mobile */}
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Enter Zipcode"
                  value={filters.zipcode}
                  onChange={(e) => handleFilterChange('zipcode', e.target.value)}
                  className="flex-1 bg-[#FDFBF7] border-[#EBE8DF] rounded-xl"
                  data-testid="mobile-zipcode-filter"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={detectLocation}
                  disabled={detectingLocation}
                  className="border-[#EBE8DF] text-[#D97757]"
                  data-testid="mobile-detect-location"
                >
                  {detectingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Navigation className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="w-full text-[#D97757]"
                  data-testid="mobile-clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Results Info Bar */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <p className="text-[#596652]">
              {loading ? 'Loading...' : `${items.length} item${items.length !== 1 ? 's' : ''} found`}
            </p>
            {userLocation && !filters.maxDistance && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#8C9A76]/15 text-[#596652]">
                <Navigation className="w-3 h-3 mr-1" />
                Sorted by distance
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <div className="flex items-center gap-2 flex-wrap">
              {filters.category && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8C9A76]/15 text-[#596652]">
                  {filters.category}
                  <button onClick={() => handleFilterChange('category', '')} className="ml-1.5 hover:text-[#D97757]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.neighborhood && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8C9A76]/15 text-[#596652]">
                  <MapPin className="w-3 h-3 mr-1" />
                  {filters.neighborhood}
                  <button onClick={() => handleFilterChange('neighborhood', '')} className="ml-1.5 hover:text-[#D97757]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.zipcode && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#D97757]/15 text-[#D97757]">
                  <Navigation className="w-3 h-3 mr-1" />
                  {filters.zipcode}
                  <button onClick={() => handleFilterChange('zipcode', '')} className="ml-1.5 hover:text-[#C56547]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.maxDistance && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#D97757]/15 text-[#D97757]">
                  <MapPin className="w-3 h-3 mr-1" />
                  {DISTANCE_OPTIONS.find((o) => o.value === filters.maxDistance)?.label || `Within ${filters.maxDistance} km`}
                  <button onClick={() => handleFilterChange('maxDistance', '')} className="ml-1.5 hover:text-[#C56547]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-white rounded-2xl border border-[#EBE8DF] overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-[#EBE8DF]"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-[#EBE8DF] rounded w-1/4"></div>
                  <div className="h-5 bg-[#EBE8DF] rounded w-3/4"></div>
                  <div className="h-4 bg-[#EBE8DF] rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'map' ? (
          <ItemMapView items={items} userLocation={userLocation} />
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="items-grid">
            {items.map((item) => (
              <ItemCard key={item.id} item={item} wishlisted={wishlistIds.includes(item.id)} onWishlistChange={handleWishlistChange} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#EBE8DF]">
            <p className="text-[#596652] mb-2">No items found</p>
            <p className="text-[#596652]/70 text-sm mb-4">Try adjusting your filters or search term</p>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-[#EBE8DF] rounded-full"
                data-testid="no-results-clear-filters"
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowsePage;
